/**
 * CashStatementsTab — shows the parse_jobs (statement imports) that
 * touched this account. We don't store the raw PDF; we track:
 *   • filename
 *   • kind (bank-statement / credit-card-statement / etc)
 *   • status + processing outcome
 *   • uploaded-at + finished-at
 *   • transaction count parsed / imported
 *   • pass/fail warning
 *
 * Association strategy:
 *   For each of the user's parse_jobs, we look at the `transactions`
 *   table for rows where `import_job_id = pj.id AND account_id = us`.
 *   The account is considered "touched" by a job if it has ≥1 such row.
 *   For jobs still in progress we optimistically show any that name the
 *   current account in `result.account_id`.
 *
 * No writes. Follow the header "Import statement" button to upload new.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { FileText, CheckCircle2, AlertTriangle, Clock, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

function fmtDateTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

function statusPill(status) {
  const map = {
    done:       { icon: CheckCircle2, label: 'Imported',   color: '#3D7A63', bg: '#DFEEE5' },
    consumed:   { icon: CheckCircle2, label: 'Imported',   color: '#3D7A63', bg: '#DFEEE5' },
    processing: { icon: Clock,        label: 'Processing', color: '#B07210', bg: '#FBF3D9' },
    queued:     { icon: Clock,        label: 'Queued',     color: '#B07210', bg: '#FBF3D9' },
    failed:     { icon: AlertTriangle, label: 'Failed',    color: '#A84E38', bg: '#F6E1DB' },
    canceled:   { icon: AlertTriangle, label: 'Cancelled', color: '#7A7A70', bg: '#EAE8DE' },
  };
  return map[status] || map.queued;
}

export default function CashStatementsTab({ account }) {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        // 1) Find all import_job_ids on transactions tied to this account.
        //    We paginate up to 2000 recent rows — that's plenty for a
        //    long-lived account.
        const { data: txns } = await supabase
          .from('transactions')
          .select('import_job_id')
          .eq('account_id', account.id)
          .not('import_job_id', 'is', null)
          .limit(2000);
        const jobIds = Array.from(new Set((txns || []).map((r) => r.import_job_id).filter(Boolean)));

        if (jobIds.length === 0) {
          if (!cancelled) { setRows([]); setLoading(false); }
          return;
        }

        const { data: jobs } = await supabase
          .from('parse_jobs')
          .select('id, filename, kind, status, created_at, finished_at, result')
          .in('id', jobIds)
          .order('created_at', { ascending: false });

        if (!cancelled) setRows(jobs || []);
      } catch (e) {
        console.warn('[CashStatementsTab] load failed:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [account?.id]);

  const summary = useMemo(() => {
    const imported = rows.filter((r) => r.status === 'done' || r.status === 'consumed').length;
    const totalTxns = rows.reduce((s, r) => {
      const res = r.result || {};
      const n = res.count || (Array.isArray(res.transactions) ? res.transactions.length : 0);
      return s + n;
    }, 0);
    return { imported, totalTxns, total: rows.length };
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="accounts-cash-tab-statements">
      {/* Summary strip */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {loading
            ? 'Loading statements…'
            : summary.total === 0
              ? 'No statement imports touch this account yet.'
              : `${summary.total} statement${summary.total !== 1 ? 's' : ''} · ${summary.totalTxns} transactions imported`}
        </p>
        <Button size="sm" variant="outline" onClick={() => navigate('/import')} className="rounded-lg" data-testid="cash-statements-upload">
          <Upload className="h-3.5 w-3.5 mr-1.5" />
          Import a statement
        </Button>
      </div>

      <section className="bg-card rounded-xl border border-border overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-5 space-y-3">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : rows.length === 0 ? (
          <div className="px-5 py-12 text-center" data-testid="cash-statements-empty">
            <FileText className="h-6 w-6 text-muted-foreground/60 mx-auto mb-2" />
            <p className="text-sm text-charcoal/70 mb-1">No statements yet</p>
            <p className="text-xs text-muted-foreground">
              When you import a PDF statement and it produces transactions for this account, we&apos;ll list it here.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border/40">
            {rows.map((r) => {
              const status = statusPill(r.status);
              const Icon = status.icon;
              const res = r.result || {};
              const count = res.count || (Array.isArray(res.transactions) ? res.transactions.length : 0);
              const warning = res.warning || null;
              return (
                <li key={r.id} className="flex items-center gap-4 px-5 py-3.5" data-testid={`cash-statement-${r.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-offwhite flex items-center justify-center shrink-0">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-charcoal truncate">{r.filename || 'Statement upload'}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {r.kind || 'statement'} · Uploaded {fmtDateTime(r.created_at)}
                      {r.finished_at && r.finished_at !== r.created_at && (
                        <> · Finished {fmtDateTime(r.finished_at)}</>
                      )}
                    </p>
                    {warning && (
                      <p className="text-[11px] mt-0.5" style={{ color: '#A84E38' }}>{warning}</p>
                    )}
                  </div>
                  {count > 0 && (
                    <span className="text-[11px] text-muted-foreground shrink-0">
                      <span className="font-medium text-charcoal num">{count}</span> txns
                    </span>
                  )}
                  <span
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium shrink-0"
                    style={{ backgroundColor: status.bg, color: status.color }}
                  >
                    <Icon className="h-3 w-3" />
                    {status.label}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
